import { useState, useRef, useEffect } from 'react'
import { Cancel01Icon as X, Tick01Icon as Check, RotateLeft01Icon as RotateCcw, Target01Icon as Target } from 'hugeicons-react'
import { motion, AnimatePresence } from 'framer-motion'
import ReactCrop, { type Crop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { useEditorStore } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'

interface CropModalProps {
    isOpen: boolean
    onClose: () => void
}

export default function CropModal({ isOpen, onClose }: CropModalProps) {
    const { blob } = useRecordingStore()
    const { videoCrop, setVideoCrop, aspectRatio: editorAspectRatio } = useEditorStore()

    const [frameUrl, setFrameUrl] = useState<string>('')
    const [crop, setCrop] = useState<Crop>({
        unit: '%',
        x: 0,
        y: 0,
        width: 100,
        height: 100
    })

    const imgRef = useRef<HTMLImageElement>(null)

    // Calculate aspect ratio for the crop box
    const [num, den] = editorAspectRatio.split('/').map(Number)
    const cropAspectRatio = num / den

    // Extract a frame from the video
    useEffect(() => {
        if (isOpen && blob) {
            const video = document.createElement('video')
            video.src = URL.createObjectURL(blob)
            video.currentTime = 1

            video.onseeked = () => {
                const canvas = document.createElement('canvas')
                canvas.width = video.videoWidth
                canvas.height = video.videoHeight
                const ctx = canvas.getContext('2d')
                if (ctx) {
                    ctx.drawImage(video, 0, 0)
                    setFrameUrl(canvas.toDataURL('image/jpeg'))
                }
                URL.revokeObjectURL(video.src)
            }
        }

        // Initialize with default optimized crop if no existing crop
        if (videoCrop) {
            setCrop({
                unit: '%',
                x: videoCrop.x,
                y: videoCrop.y,
                width: videoCrop.width,
                height: videoCrop.height
            })
        } else {
            // Default: Maximize crop within frame
            // If target (cropAspectRatio) is wider than video (1 since we use %), 
            // then width=100, height depends on video ratio.
            // Wait, since we use %, we need the video's actual aspect ratio.
        }
    }, [isOpen, blob, videoCrop])

    // Helper to calculate a crop that fills the target aspect ratio as much as possible
    const calculateOptimalCrop = (videoWidth: number, videoHeight: number, targetAR: number) => {
        const videoAR = videoWidth / videoHeight
        const newCrop: Crop = { unit: '%', x: 0, y: 0, width: 0, height: 0 }

        if (targetAR > videoAR) {
            // Target is wider than video (e.g. 16:9 frame on a vertical video)
            newCrop.width = 100
            newCrop.height = (videoAR / targetAR) * 100
            newCrop.x = 0
            newCrop.y = (100 - newCrop.height) / 2
        } else {
            // Target is narrower than video (e.g. vertical frame on a 16:9 video)
            newCrop.height = 100
            newCrop.width = (targetAR / videoAR) * 100
            newCrop.y = 0
            newCrop.x = (100 - newCrop.width) / 2
        }
        return newCrop
    }

    const handleImageLoad = (_e: React.SyntheticEvent<HTMLImageElement>) => {
        if (videoCrop) return
        // Default to full frame — user can click "Fit to Frame" to auto-crop to aspect ratio
        setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 })
    }

    const handleApply = () => {
        if (crop.width && crop.height) {
            setVideoCrop({
                x: crop.x || 0,
                y: crop.y || 0,
                width: crop.width,
                height: crop.height
            })
        }
        onClose()
    }

    const handleFitToFrame = () => {
        if (imgRef.current) {
            const { width, height } = imgRef.current
            setCrop(calculateOptimalCrop(width, height, cropAspectRatio))
        }
    }

    const handleReset = () => {
        setVideoCrop(null)
        if (imgRef.current) {
            const { width, height } = imgRef.current
            setCrop(calculateOptimalCrop(width, height, cropAspectRatio))
        }
    }

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-surface-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden w-full max-w-5xl max-h-[90vh] flex flex-col"
                >
                    {/* Header */}
                    <div className="h-14 border-b border-white/10 flex items-center justify-between px-6 bg-surface-900">
                        <div className="flex items-center gap-3">
                            <h2 className="text-sm font-medium text-white">Crop Video</h2>
                            <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] text-txt-dim font-mono">
                                {editorAspectRatio}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleFitToFrame}
                                className="px-3 py-1.5 hover:bg-white/10 rounded-lg text-xs text-txt-dim hover:text-white transition-colors flex items-center gap-2"
                                title="Center and fit to current aspect ratio"
                            >
                                <Target size={14} />
                                Fit to Frame
                            </button>
                            <button
                                onClick={handleReset}
                                className="p-2 hover:bg-white/10 rounded-lg text-txt-dim hover:text-white transition-colors"
                                title="Reset to Original"
                            >
                                <RotateCcw size={18} />
                            </button>
                            <div className="w-px h-4 bg-white/10 mx-1" />
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/10 rounded-lg text-txt-dim hover:text-white transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Crop Area */}
                    <div className="flex-1 overflow-auto p-12 flex items-center justify-center bg-black/60 relative">
                        {frameUrl ? (
                            <div className="relative group">
                                <ReactCrop
                                    crop={crop}
                                    onChange={(_, pc) => setCrop(pc)}
                                    aspect={cropAspectRatio}
                                    className="max-h-[60vh] rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10"
                                >
                                    <img
                                        ref={imgRef}
                                        src={frameUrl}
                                        alt="Video frame"
                                        className="max-h-[60vh] block"
                                        onLoad={handleImageLoad}
                                    />
                                </ReactCrop>

                                {/* Guideline Overlay */}
                                <div className="absolute -bottom-8 left-0 right-0 text-center pointer-events-none">
                                    <p className="text-[10px] text-txt-dim uppercase tracking-widest font-bold">Adjust bounds to focus on content</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-12 h-12 rounded-full border-2 border-brand-500/20 border-t-brand-500 animate-spin" />
                                <div className="text-txt-dim text-sm animate-pulse">Processing cinematic frame...</div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-5 border-t border-white/10 flex justify-between items-center bg-surface-900/50 backdrop-blur">
                        <div className="flex flex-col gap-1">
                            <div className="text-xs font-medium text-white flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-brand-500" />
                                Custom Selection
                            </div>
                            <div className="text-[10px] text-txt-dim font-mono">
                                {crop.width && crop.height && (
                                    <>AREA: {crop.width.toFixed(1)}% × {crop.height.toFixed(1)}%</>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={onClose}
                                className="px-6 py-2 text-sm font-medium text-txt-secondary hover:text-white transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApply}
                                className="px-6 py-2 bg-brand-500 hover:bg-brand-400 text-white rounded-xl text-sm font-semibold flex items-center gap-2 shadow-lg shadow-brand-500/20 active:scale-95 transition-all"
                            >
                                <Check size={16} strokeWidth={3} />
                                Apply Crop
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
